import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatFolder } from "@scrile/api-provider/dist/api/ChatFoldersProvider";
import providers from "lib/providers";

export type { ChatFolder } from "@scrile/api-provider/dist/api/ChatFoldersProvider";

// localStorage cache so the folder tabs render on first paint instead of
// popping in after the network round-trip (folders are a tiny JSON blob).
const foldersCacheKey = (userId: string | number) => `smeet_chat_folders:${userId}`;

function readCachedFolders(userId: string | number): ChatFolder[] {
  try {
    const raw = localStorage.getItem(foldersCacheKey(userId));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function useChatFolders(userId: string | number | undefined) {
  const [folders, setFolders] = useState<ChatFolder[]>(() => (userId ? readCachedFolders(userId) : []));
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Only persist after the first successful network load — otherwise the
  // initial empty state would clobber a valid cache.
  const hasLoadedRef = useRef(false);
  // Live user id: a refresh that raced an in-place account swap must not
  // apply the previous account's folders (they'd get persisted under the
  // NEW user's cache key).
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const result = await providers.ChatFoldersProvider.getAll();
      if (String(userIdRef.current) !== String(userId)) return;
      hasLoadedRef.current = true;
      setFolders(result || []);
    } catch (e) {
      console.error("Failed to load chat folders:", e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Hydrate from cache when the user id becomes known after mount. Also
  // resets the loaded flag on an in-place account swap so the persist effect
  // below can't write the PREVIOUS user's folders under the new user's key.
  useEffect(() => {
    if (!userId) return;
    hasLoadedRef.current = false;
    setFolders(readCachedFolders(userId));
  }, [userId]);

  // Write-through: every confirmed change (network refresh or mutation)
  // lands in the cache automatically.
  useEffect(() => {
    if (!userId || !hasLoadedRef.current) return;
    try {
      localStorage.setItem(foldersCacheKey(userId), JSON.stringify(folders));
    } catch {
      /* quota/private mode — cache is best-effort */
    }
  }, [folders, userId]);

  // A mutation that resolves AFTER an in-place account switch must not write
  // the previous user's folders into the new user's state (the write-through
  // effect would then persist them under the new user's cache key).
  const isStaleUser = (uid: string | number | undefined) => String(userIdRef.current) !== String(uid);

  const createFolder = useCallback(async (name: string): Promise<ChatFolder | null> => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const uid = userIdRef.current;
    try {
      const created = await providers.ChatFoldersProvider.create({ name: trimmed });
      if (isStaleUser(uid)) return null;
      setFolders((prev) => [...prev, created]);
      return created;
    } catch (e) {
      console.error("Failed to create folder:", e);
      return null;
    }
  }, []);

  const renameFolder = useCallback(async (folderId: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const uid = userIdRef.current;
    try {
      const updated = await providers.ChatFoldersProvider.rename({ id: folderId, name: trimmed });
      if (isStaleUser(uid)) return;
      setFolders((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
    } catch (e) {
      console.error("Failed to rename folder:", e);
    }
  }, []);

  const deleteFolder = useCallback(async (folderId: string) => {
    const uid = userIdRef.current;
    try {
      await providers.ChatFoldersProvider.deleteFolder({ id: folderId });
      if (isStaleUser(uid)) return;
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      setActiveFolderId((prev) => (prev === folderId ? null : prev));
    } catch (e) {
      console.error("Failed to delete folder:", e);
    }
  }, []);

  const toggleThreadInFolder = useCallback(
    async (folderId: string, threadId: string) => {
      const tid = String(threadId);

      // Optimistic update first
      setFolders((prev) =>
        prev.map((f) => {
          if (f.id !== folderId) return f;
          const has = f.threadIds.includes(tid);
          return {
            ...f,
            threadIds: has ? f.threadIds.filter((id) => id !== tid) : [...f.threadIds, tid],
          };
        })
      );

      // Then sync with server
      const uid = userIdRef.current;
      try {
        const currentFolder = folders.find((f) => f.id === folderId);
        const isInFolder = currentFolder?.threadIds.includes(tid);
        const updated = isInFolder
          ? await providers.ChatFoldersProvider.removeThread({ folderId, threadId: tid })
          : await providers.ChatFoldersProvider.addThread({ folderId, threadId: tid });
        if (isStaleUser(uid)) return; // account switched mid-flight
        // Apply server response (source of truth)
        setFolders((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      } catch (e) {
        console.error("Failed to toggle thread in folder:", e);
        // Revert on error
        refresh();
      }
    },
    [folders, refresh]
  );

  // Memoized map: threadId -> folderIds for efficient lookup
  const threadFolderMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const folder of folders) {
      for (const tid of folder.threadIds) {
        const key = String(tid);
        if (!map[key]) map[key] = [];
        map[key].push(folder.id);
      }
    }
    return map;
  }, [folders]);

  const getThreadFolderIds = useCallback(
    (threadId: string): string[] => {
      return threadFolderMap[String(threadId)] || [];
    },
    [threadFolderMap]
  );

  return {
    folders,
    activeFolderId,
    setActiveFolderId,
    createFolder,
    renameFolder,
    deleteFolder,
    toggleThreadInFolder,
    getThreadFolderIds,
    loading,
    refresh,
  };
}
